import { motion } from 'framer-motion';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Calendar, MapPin, ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/lib/index';
import { ROUTE_PATHS } from '@/lib/index';
import { newsArticles, formatDate, type Language } from '@/data/newsData';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const backLabel: Record<Language, string> = {
  'zh-CN': '返回动态列表',
  'zh-TW': '返回動態列表',
  'en': 'Back to News',
  'ja': 'ニュース一覧に戻る',
  'ko': '뉴스 목록으로',
};
const breadcrumbNews: Record<Language, string> = {
  'zh-CN': '公司动态',
  'zh-TW': '公司動態',
  'en': 'Company News',
  'ja': '会社ニュース',
  'ko': '회사 소식',
};

export default function CompanyNewsDetail() {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const lang = (language as Language) in backLabel ? (language as Language) : 'en';

  const article = newsArticles.find((a) => a.id === id);
  if (!article) return <Navigate to={ROUTE_PATHS.NEWS} replace />;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative bg-foreground text-background py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-primary blur-2xl -translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-background/60 mb-8"
          >
            <Link to={ROUTE_PATHS.HOME} className="hover:text-primary transition-colors">
              {lang === 'zh-CN' ? '首页' : lang === 'zh-TW' ? '首頁' : lang === 'ja' ? 'ホーム' : lang === 'ko' ? '홈' : 'Home'}
            </Link>
            <span>/</span>
            <Link to={ROUTE_PATHS.NEWS} className="hover:text-primary transition-colors">
              {breadcrumbNews[lang]}
            </Link>
            <span>/</span>
            <span className="text-background/90 truncate max-w-xs">{article.personName}</span>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={stagger}>
            {/* Tag + meta */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-block px-3 py-1 bg-primary/20 text-primary text-xs font-semibold tracking-widest rounded-full uppercase">
                {article.tag[lang]}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-background/60">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(article.date, lang)}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-background/60">
                <MapPin className="w-3.5 h-3.5" />
                {article.location[lang]}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              variants={fadeInUp}
              className="text-3xl md:text-5xl font-bold text-background leading-tight max-w-3xl"
            >
              {article.title[lang]}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Article body */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-3 gap-10">

            {/* Sidebar: photo + person card */}
            <motion.aside
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <div className="sticky top-28 space-y-5">
                {/* Photo */}
              <div className={`rounded-2xl overflow-hidden bg-muted relative ${article.imageAspect === 'landscape' ? 'aspect-[16/9]' : 'aspect-[3/4]'}`}>
                  <img
                    src={article.image}
                    alt={article.personName}
                    className={`absolute inset-0 w-full h-full object-cover ${article.imageAspect === 'landscape' ? 'object-center' : 'object-top'}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-background font-bold text-lg">{article.personName}</p>
                    <p className="text-primary text-sm font-medium leading-snug mt-0.5">
                      {article.personTitle[lang]}
                    </p>
                  </div>
                </div>

                {/* Back link */}
                <Link
                  to={ROUTE_PATHS.NEWS}
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary font-medium transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {backLabel[lang]}
                </Link>
              </div>
            </motion.aside>

            {/* Main article */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              variants={stagger}
              className="lg:col-span-2 space-y-8"
            >
              {/* Lead paragraph */}
              <motion.p
                variants={fadeInUp}
                className="text-base md:text-lg text-foreground font-medium leading-relaxed border-l-4 border-primary pl-5"
              >
                {article.excerpt[lang]}
              </motion.p>

              {/* Body sections */}
              {article.sections.map((section, idx) => (
                <motion.div key={idx} variants={fadeInUp} className="space-y-3">
                  <h2 className="text-lg md:text-xl font-bold text-foreground flex items-center gap-2">
                    <span className="w-1 h-5 bg-primary rounded-full inline-block flex-shrink-0" />
                    {section.heading[lang]}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed pl-4">
                    {section.body[lang]}
                  </p>
                </motion.div>
              ))}

              {/* CEO Quote */}
              <motion.blockquote
                variants={fadeInUp}
                className="border-l-4 border-primary pl-6 py-2 my-2 bg-primary/5 rounded-r-xl"
              >
                <p className="text-foreground italic leading-relaxed font-medium">
                  "{article.ceoQuote[lang]}"
                </p>
                <footer className="mt-3 text-xs text-muted-foreground font-semibold tracking-wide">
                  {article.ceoQuoteAttrib[lang]}
                </footer>
              </motion.blockquote>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
